import { Inertia } from '@inertiajs/inertia'
import { Component, h, Host, Listen, Prop, State, Watch } from '@stencil/core/internal'

import { SharedProps } from '../../app'
import { IShareNote } from '../../share/types'
import { Deferred } from '../../utils/async'
import { colorClasses } from '../../utils/css'

@Component({
  tag: 'page-share',
})
export class Share {
  @Prop() pusherConfig: Array<{ channel: string; event: string }>
  @Prop() pusherClient: typeof Pusher
  @Prop() note: IShareNote
  @Prop() env: SharedProps['env']

  @State() displayMode = 'normal'

  bootstrap: Deferred<any>
  videoPlayerEl: HTMLVideoPlayerElement

  @Watch('pusherClient')
  @Watch('pusherConfig')
  initPusher() {
    if (this.pusherClient && this.pusherConfig) {
      const channelClientMap = {}
      this.pusherConfig.map(({ channel, event }) => {
        let c = channelClientMap[channel]
        if (!c) {
          c = this.pusherClient.subscribe(channel)
          channelClientMap[channel] = c
        }
        c.bind(event, (_) => {
          if (event.startsWith('note-updated')) {
            Inertia.reload({ only: ['note'] })
          } else if (event.startsWith('attached-hls-playlist')) {
            // TODO: how to reload at time?
            this.videoPlayerEl.reload(this.note.start_time)
          }
        })
      })
    }
  }

  componentWillLoad() {
    this.bootstrap = new Deferred()
    const app = document.querySelector('inertia-app')
    app && app.waitFor(this.bootstrap.promise)
  }

  componentDidLoad() {
    this.initPusher()
  }

  // https://caniuse.com/fullscreen
  videoPlayerExitFullScreen() {
    const el = document as any
    const cancelFunc = el.exitFullscreen || el.webkitCancelFullScreen
    if (cancelFunc) {
      cancelFunc.apply(el)
    }
  }

  // https://caniuse.com/fullscreen
  videoPlayerEnterFullScreen() {
    const el = this.videoPlayerEl as any
    const requestFunc = el.requestFullscreen || el.webkitRequestFullScreen
    if (requestFunc) {
      requestFunc.apply(el)
    }
  }

  // https://caniuse.com/fullscreen
  videoPlayerInFullScreen(): boolean {
    const el = document as any
    return !!el.fullscreenElement || !!el.webkitFullscreenElement
  }

  @Listen('fullscreenchange', { target: 'document' })
  @Listen('webkitfullscreenchange', { target: 'document' })
  videoPlayerFullScreenChanged() {
    this.displayMode = this.videoPlayerInFullScreen() ? 'fullscreen' : 'normal'
  }

  render() {
    return (
      <Host class='flex overflow-hidden flex-col m-auto w-full max-w-4xl spacing-y-4'>
        <div class='flex flex-col bg-black rounded-lg'>
          <video-player
            trackingSessionId={'share-page-' + this.note?.call?.id}
            muxVideoTitle={'Share Page - ' + this.note?.call?.id}
            muxVideoId={`${this.note?.call?.id}`}
            muxPageType='share-page'
            class='relative w-full'
            src={this.note.video_url}
            startTime={this.note.start_time}
            endTime={this.note.end_time}
            onMetadataLoaded={() => this.bootstrap.resolve()}
            ref={(el) => {
              if (el) {
                this.videoPlayerEl = el
              }
            }}
            renderOverlay={() => (
              <div class='absolute top-0 right-0 left-0 m-3'>
                <div class='flex justify-between'>
                  <video-call-chip call={this.note.call} avatar date />
                  {this.note.call.permalink && (
                    <div class='flex flex-col'>
                      <ui-button
                        href={this.note.call.permalink}
                        target='_blank'
                        color='link'
                        icon='arrow-right-short'
                        iconRight={true}
                        iconStyle={{ fontSize: '1.5em', marginLeft: '0px' }}
                        class={`font-bold px-8px`}
                        onClick={(e) => e.stopImmediatePropagation()}
                      >
                        View Call
                      </ui-button>
                    </div>
                  )}
                </div>
              </div>
            )}
            renderActions={() => (
              <ui-icon
                slot='action'
                class='z-menu text-lg cursor-pointer'
                icon={this.displayMode !== 'normal' ? 'arrows-angle-contract' : 'arrows-angle-expand'}
                onClick={() => {
                  if (this.videoPlayerInFullScreen()) {
                    this.videoPlayerExitFullScreen()
                  } else {
                    this.videoPlayerEnterFullScreen()
                  }
                }}
              />
            )}
          />
          <div class='flex py-4 pl-4 text-lg'>
            <span class={`font-bold whitespace-no-wrap ${colorClasses({ text: this.note.hex_color })}`}>
              {this.note.label}
            </span>
            {this.note.text && <span class='px-2 text-white'>&ndash;</span>}
            {this.note.text && <span class='text-white'>{this.note.text}</span>}
          </div>
        </div>
      </Host>
    )
  }
}
